interface UnmetItem {
  outcome: string;
  label: string;
  action: string;
  contacts: number;
}

interface ConversionRate {
  count: number;
  total: number;
  rate: number | null;
  reliable: boolean;
}

/**
 * Conversión y demanda insatisfecha.
 *
 * Dos reglas de presentación:
 *
 * 1. **La tasa nunca va sola.** "73%" sobre 11 casos es ruido con cara de
 *    precisión. Se muestra siempre el "X de Y", y por debajo de 30 casos se
 *    dice que hay pocos datos en vez de inventar un decimal.
 * 2. **Cada número trae su acción.** Un contador sin nada que hacer con él no
 *    es un KPI, es decoración.
 */
interface Effort {
  medianMessagesToConvert: number | null;
  medianMessagesWithout: number | null;
  convertedSample: number;
  withoutSample: number;
}

/** Con menos de 5 casos una mediana no dice nada; se calla en vez de mentir. */
const MIN_MUESTRA = 5;

export function UnmetDemandPanel({
  conversion,
  unmetDemand,
  effort,
}: {
  conversion: ConversionRate;
  unmetDemand: UnmetItem[];
  effort?: Effort;
}) {
  const perdidas = unmetDemand.reduce((acc, u) => acc + u.contacts, 0);
  const pct = conversion.rate === null ? null : Math.round(conversion.rate * 100);

  const mostrarEsfuerzo =
    effort?.medianMessagesToConvert != null && effort.convertedSample >= MIN_MUESTRA;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-5">
        <p className="text-caption text-neutral-500 dark:text-neutral-400">Lograron su objetivo</p>
        <p className="mt-1 flex items-baseline gap-2">
          <span className="text-3xl font-bold tabular-nums text-neutral-900 dark:text-neutral-100">
            {conversion.count}
          </span>
          <span className="text-body-sm text-neutral-500 dark:text-neutral-400">
            de {conversion.total} personas
          </span>
        </p>
        {pct !== null && conversion.reliable && (
          <p className="mt-1 text-body-sm font-medium text-success-700 dark:text-success-300">
            {pct}% de conversión
          </p>
        )}
        {pct !== null && !conversion.reliable && (
          <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
            Pocos datos todavía para una tasa confiable (hacen falta 30 personas; van{' '}
            {conversion.total}).
          </p>
        )}
      </div>

      <div className="rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-5">
        <p className="text-caption text-neutral-500 dark:text-neutral-400">
          Se fueron sin lo que buscaban
        </p>
        <p className="mt-1 text-3xl font-bold tabular-nums text-neutral-900 dark:text-neutral-100">
          {perdidas}
        </p>

        {unmetDemand.length === 0 ? (
          <p className="mt-2 text-body-sm text-neutral-500 dark:text-neutral-400">
            Nadie quedó sin atención en este período.
          </p>
        ) : (
          <ul className="mt-3 space-y-2">
            {unmetDemand.map((u) => (
              <li key={u.outcome} className="flex items-start justify-between gap-3">
                <span>
                  <span className="block text-body-sm text-neutral-800 dark:text-neutral-200">
                    {u.label}
                  </span>
                  <span className="block text-xs text-neutral-500 dark:text-neutral-400">
                    {u.action}
                  </span>
                </span>
                <span className="text-body-sm font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
                  {u.contacts}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-5">
        <p className="text-caption text-neutral-500 dark:text-neutral-400">
          Mensajes para lograrlo
        </p>
        {mostrarEsfuerzo && effort ? (
          <>
            <p className="mt-1 flex items-baseline gap-2">
              <span className="text-3xl font-bold tabular-nums text-neutral-900 dark:text-neutral-100">
                {effort.medianMessagesToConvert}
              </span>
              <span className="text-body-sm text-neutral-500 dark:text-neutral-400">
                típicamente
              </span>
            </p>
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              Mediana sobre {effort.convertedSample} personas que lo lograron.
              {effort.medianMessagesWithout != null &&
                ` Quienes no lo lograron escribieron ${effort.medianMessagesWithout}.`}
            </p>
            <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
              Si este número sube, apareció fricción antes de que alguien se queje.
            </p>
          </>
        ) : (
          <p className="mt-2 text-body-sm text-neutral-500 dark:text-neutral-400">
            Hacen falta al menos {MIN_MUESTRA} personas que hayan logrado el objetivo para que la
            mediana signifique algo.
          </p>
        )}
      </div>
    </div>
  );
}
